import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useLang } from '../../i18n/LanguageContext';
import { PLATFORM_PAGES } from '../../config/platformPages';
import { PRICE_RANGE, TRIAL_DAYS } from '../../config/plans';
import paperBrainzLogo from '../../assets/PaperBrainzLogo.png';
import paperBrainzWhiteLogo from '../../assets/PaperBrainzWhiteLogo.png';

// The single footer for every public page, homepage included. The homepage previously
// rendered its own inline version, so users saw a different footer depending on where
// they landed. Anything added here appears site-wide — which is also what gives the
// standalone marketing pages their internal links.
function LandingFooter({ isLandingPage = false }) {
  const { theme } = useTheme();
  const { t } = useLang();
  const d = theme === 'dark';

  // Section anchors only exist on the homepage, so route home first from anywhere else.
  const sectionHref = (id) => (isLandingPage ? `#${id}` : `/#${id}`);

  const linkClass = `transition-colors ${d ? 'hover:text-white' : 'hover:text-gray-900'}`;
  const headingClass = `font-medium mb-4 text-sm ${d ? 'text-white' : 'text-gray-900'}`;

  const columns = [
    {
      heading: t('footer.product'),
      links: [
        { href: sectionHref('how-it-works'), label: t('nav.howItWorks') },
        { href: sectionHref('features'), label: t('nav.features') },
        { to: '/pricing', label: t('nav.pricing') },
        { href: sectionHref('faq'), label: t('nav.faq') },
      ],
    },
    {
      heading: t('footer.integrations'),
      links: PLATFORM_PAGES.map((p) => ({ to: p.path, label: p.platform })),
    },
    {
      heading: t('footer.legal'),
      links: [
        { to: '/security', label: t('footer.security') },
        { to: '/privacy-policy', label: t('footer.privacy') },
        { to: '/terms-of-service', label: t('footer.terms') },
        { to: '/refund-policy', label: t('footer.refund') },
        { to: '/cookie-policy', label: t('footer.cookie') },
      ],
    },
    {
      heading: t('footer.account'),
      links: [
        { to: '/login', label: t('footer.login') },
        { to: '/register', label: t('footer.signup') },
        { to: '/dashboard', label: t('footer.dashboardLink') },
        { href: 'mailto:support@paperbrainz.com', label: t('footer.support'), external: true },
      ],
    },
  ];

  return (
    <footer
      className={`py-14 px-4 sm:px-6 lg:px-8 border-t transition-colors duration-300 ${
        d ? 'bg-[#0a0a0a] border-white/[0.06] text-gray-500' : 'bg-white border-gray-200 text-gray-500'
      }`}
    >
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 gap-y-10 mb-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <Link to="/" className="flex items-center space-x-3 mb-4" aria-label="PaperBrainz home">
              <img
                src={d ? paperBrainzLogo : paperBrainzWhiteLogo}
                alt="PaperBrainz - AI Customer Support Chatbot"
                className="w-7 h-7"
              />
              <span className={`text-base font-medium ${d ? 'text-white' : 'text-gray-900'}`}>PaperBrainz</span>
            </Link>
            <p className="text-sm font-light mb-4 max-w-xs">{t('footer.tagline')}</p>
            <a
              href="mailto:support@paperbrainz.com"
              className={`text-sm font-light ${d ? 'text-gray-500 hover:text-white' : 'text-gray-500 hover:text-gray-900'} transition-colors`}
            >
              support@paperbrainz.com
            </a>
          </div>

          {columns.map((col) => (
            <div key={col.heading}>
              <h4 className={headingClass}>{col.heading}</h4>
              <ul className="space-y-2.5 text-sm font-light">
                {col.links.map((link) => (
                  <li key={link.label}>
                    {link.to ? (
                      <Link to={link.to} className={linkClass}>
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        className={linkClass}
                        {...(link.external ? {} : {})}
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Trial reminder — the footer is the last thing a hesitant visitor reads */}
        <div
          className={`rounded-[16px] border px-5 py-4 mb-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${
            d ? 'border-white/[0.06] bg-white/[0.02]' : 'border-gray-200 bg-gray-50'
          }`}
        >
          <p className={`text-sm font-light ${d ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('footer.ctaText').replace('{days}', TRIAL_DAYS).replace('{price}', PRICE_RANGE.low)}
          </p>
          <Link
            to="/register"
            className={`text-sm font-medium px-5 py-2.5 rounded-full text-center whitespace-nowrap transition-colors ${
              d ? 'bg-white text-black hover:bg-gray-200' : 'bg-gray-900 text-white hover:bg-black'
            }`}
          >
            {t('nav.startFreeTrial')}
          </Link>
        </div>

        <div
          className={`border-t pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm font-light ${
            d ? 'border-white/[0.06]' : 'border-gray-200'
          }`}
        >
          <p className={d ? 'text-gray-700' : 'text-gray-500'}>
            &copy; {new Date().getFullYear()} PaperBrainz. {t('footer.rights')}
          </p>
          <a
            href="https://linkedin.com/company/paperbrainz"
            target="_blank"
            rel="noopener noreferrer"
            className={`transition-colors ${d ? 'text-gray-700 hover:text-white' : 'text-gray-400 hover:text-gray-900'}`}
            aria-label="PaperBrainz on LinkedIn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
          </a>
        </div>
      </div>
    </footer>
  );
}

export default LandingFooter;
